import { Id, useToasts } from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFilterQuery } from "../api/filterQueries";
import { ActiveFilterItem } from "./ActiveFilterItem";
import { useFilter } from "./FilterProvider";
import { FilterItem, OnDemandFilterFormValues } from "./filterTypes";

const FilterBarItems = styled.div`
	padding: ${({ theme }) => theme.spacing(2)} 0;
	display: flex;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
	filterId: Id | null;
	onRemove: () => void;
	onClick: () => void;
}

export const FilterBar: FC<Props> = ({ filterId, onRemove, onClick }) => {
	const { t } = useTranslation();
	const { applyFilter } = useFilter();
	const { showToast } = useToasts();
	const { data: filter, isLoading } = useFilterQuery({
		filterId,
		onNotFound: () =>
			showToast({
				type: "warning",
				title: t("Filter"),
				body: t("The filter no longer exists and cannot be applied."),
			}),
	});

	const removeFilterItem = useCallback(
		async (filterItem: FilterItem) => {
			if (!filter) {
				return;
			}

			const newFilter: OnDemandFilterFormValues = {
				mode: filter.mode,
				filterItems: [
					...filter.filterItems
						.filter((x) => x !== filterItem)
						.map(
							(item): FilterItem => ({
								...item,
							}),
						),
				],
			};

			if (newFilter.filterItems.length === 0) {
				onRemove();
				return;
			}

			applyFilter(newFilter);
		},
		[onRemove, applyFilter, filter],
	);

	if (filter === undefined || !filter || isLoading) {
		return null;
	}

	return (
		<FilterBarItems>
			{filter.filterItems.map((item, index) => (
				<ActiveFilterItem
					key={`${item.entity}.${item.field}.${index}`}
					removeFilterItem={removeFilterItem}
					filterItem={item}
					onClick={onClick}
				/>
			))}
		</FilterBarItems>
	);
};
